import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { LexicalTypeaheadMenuPlugin } from '@lexical/react/LexicalTypeaheadMenuPlugin';
import { useQuery } from '@tanstack/react-query';
import { $insertNodes, COMMAND_PRIORITY_EDITOR, createCommand } from 'lexical';
import dynamic from 'next/dynamic';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { $createMentionNode } from 'components/TextEditor/Nodes/MentionNode';

import { merge } from 'lib/array';
import { commentQueryKeys, userQueryKeys } from 'lib/queryKeys';
import { $createWhitespace, getAtSignMentionMatch } from 'lib/textEditor';
import { filterUsersByWord } from 'lib/user';

import { useDomain } from 'providers/DomainProvider';
import { useUser } from 'providers/UserProvider';

import {
  getArticleCommentMentionsList,
  getOfferCommentMentionsList,
  getThreadCommentMentionsList,
} from 'services/comment';
import { getUserFollowingList } from 'services/user';

import MentionTypeaheadOption from './MentionTypeaheadOption';

const MentionAutocomplete = dynamic(() => import('./MentionAutocomplete'), {
  ssr: false,
});

const ENDPOINTS = {
  article: (domainId, opts) =>
    getArticleCommentMentionsList(
      {
        articleId: domainId,
      },
      opts
    ),
  offer: (domainId, opts) =>
    getOfferCommentMentionsList(
      {
        offerId: domainId,
      },
      opts
    ),
  thread: (domainId, opts) =>
    getThreadCommentMentionsList(
      {
        threadId: domainId,
      },
      opts
    ),
};
export const INSERT_MENTION_COMMAND = createCommand('mention');

const getNodesSeparatedByWhitespace = (nodes) => {
  const nodesWithWhitespace = [];

  for (const node of nodes) {
    nodesWithWhitespace.push(node, $createWhitespace());
  }

  return nodesWithWhitespace;
};

const MentionsPlugin = () => {
  const [editor] = useLexicalComposerContext();
  const [wordInSelection, setWordInSelection] = useState(null);
  const { domain, domainId } = useDomain();
  const { isLoggedIn, LoggedInUser } = useUser();

  const { data: usersFromMentions, isSuccess: isUsersFromMentionsSuccess } =
    useQuery({
      queryKey: commentQueryKeys[domain]?.mentions({ domainId }),
      queryFn: ({ signal }) =>
        (ENDPOINTS[domain] || ENDPOINTS.offer)(domainId, { signal }),
      enabled: isLoggedIn && !!domainId && !!wordInSelection,
    });

  const { data: followedUsers, isSuccess: isFollowedUsersSuccess } = useQuery({
    queryKey: userQueryKeys.followersList({ userId: LoggedInUser?.userId }),
    enabled: isLoggedIn && !!domainId && !!wordInSelection,
    queryFn: ({ signal }) => getUserFollowingList({ signal }),
  });

  useEffect(() => {
    const cleanup = editor.registerCommand(
      INSERT_MENTION_COMMAND,
      (payload) => {
        editor.update(() => {
          const nodes = [];

          if (Array.isArray(payload)) {
            payload.forEach((userUsername) => {
              nodes.push($createMentionNode(userUsername));
            });
          } else {
            nodes.push($createMentionNode(payload));
          }

          $insertNodes(getNodesSeparatedByWhitespace(nodes));
        });

        return true;
      },
      COMMAND_PRIORITY_EDITOR
    );

    return () => {
      cleanup();
    };
  }, [editor]);

  const mentionableUsers = useMemo(() => {
    if (!isUsersFromMentionsSuccess && !isFollowedUsersSuccess) {
      return [];
    }

    const users = merge(usersFromMentions, followedUsers, 'userId');

    return filterUsersByWord(users, wordInSelection).map(
      (user) => new MentionTypeaheadOption(user)
    );
  }, [
    isUsersFromMentionsSuccess,
    isFollowedUsersSuccess,
    usersFromMentions,
    followedUsers,
    wordInSelection,
  ]);

  const onSelectOption = useCallback(
    (selectedOption, nodeToReplace) => {
      editor.update(() => {
        const mentionNode = $createMentionNode(selectedOption.userUsername);

        if (nodeToReplace) {
          nodeToReplace.replace(mentionNode);
          mentionNode.select();
        }

        return true;
      });
    },
    [editor]
  );

  return (
    <LexicalTypeaheadMenuPlugin
      menuRenderFn={(_, { selectedIndex, selectOptionAndCleanUp }) => (
        <MentionAutocomplete
          mentionableUsers={mentionableUsers}
          selectedIndex={selectedIndex}
          onAddMention={(option) => selectOptionAndCleanUp(option)}
        />
      )}
      options={mentionableUsers}
      triggerFn={getAtSignMentionMatch}
      onQueryChange={setWordInSelection}
      onSelectOption={onSelectOption}
    />
  );
};

export default MentionsPlugin;
